import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { Bot, User, Send, Sparkles, Award, X, Trophy, AlertTriangle, ThumbsUp, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const PERSONAS = [
  { value: "pelit",           label: "Klien Pelit",            emoji: "💰", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "kurang_percaya",  label: "Klien Kurang Percaya",   emoji: "🤨", color: "bg-rose-100 text-rose-800 border-rose-200" },
  { value: "nawar_harga",     label: "Klien Sering Nawar Harga", emoji: "🤝", color: "bg-sky-100 text-sky-800 border-sky-200" },
  { value: "tidak_paham",     label: "Klien Tidak Paham Produk", emoji: "🤔", color: "bg-violet-100 text-violet-800 border-violet-200" },
];

function cleanMessage(text) {
  return (text || "").replace(/\[DEAL\]|\[BATAL\]/g, "").trim();
}

export default function SalesTrainingPage() {
  const [persona, setPersona] = useState("pelit");
  const [session, setSession] = useState(null); // {session_id, persona_label}
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', text}
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [outcome, setOutcome] = useState("ongoing");
  const [evalOpen, setEvalOpen] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  async function startSession() {
    setLoading(true);
    setMessages([]);
    setOutcome("ongoing");
    setEvaluation(null);
    try {
      const { data } = await api.post("/sales-sim/start", { persona });
      setSession({ session_id: data.session_id, persona_label: data.persona_label });
      setMessages([{ role: "assistant", text: cleanMessage(data.ai_message) }]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  async function send() {
    if (!draft.trim() || !session) return;
    const txt = draft.trim();
    setDraft("");
    setMessages(m => [...m, { role: "user", text: txt }]);
    setLoading(true);
    try {
      const { data } = await api.post("/sales-sim/message", { session_id: session.session_id, text: txt });
      setMessages(m => [...m, { role: "assistant", text: cleanMessage(data.ai_message) }]);
      if (data.outcome && data.outcome !== "ongoing") {
        setOutcome(data.outcome);
        toast(data.outcome === "deal" ? "🎉 Deal! Selamat, klien setuju." : "❌ Klien membatalkan.", { duration: 4500 });
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  async function endAndEvaluate() {
    if (!session) return;
    setLoading(true);
    try {
      const { data } = await api.post("/sales-sim/evaluate", { session_id: session.session_id });
      setEvaluation(data.evaluation || {});
      setOutcome(data.outcome || "evaluated");
      setEvalOpen(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  function newSession() {
    setSession(null);
    setMessages([]);
    setOutcome("ongoing");
    setEvaluation(null);
  }

  const currentPersona = PERSONAS.find(p => p.value === persona);

  return (
    <div className="space-y-6" data-testid="sales-training-page">
      <PageHeader overline="AI Coach" title="Sales Training Simulator" description="Latih kemampuan sales Anda dengan AI yang berperan sebagai klien berbagai karakter. Akhiri & dapatkan evaluasi mendalam dari AI Coach." icon={Sparkles} tone="brand" />

      {!session ? (
        <Card className="p-6 space-y-4 border-border">
          <p className="overline">Mulai Sesi Latihan</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            {PERSONAS.map(p => (
              <button
                key={p.value}
                type="button"
                onClick={() => setPersona(p.value)}
                className={`rounded-lg border-2 p-4 text-left transition-all ${persona === p.value ? "border-brand bg-brand/5 shadow-md" : "border-border bg-card hover:border-brand/40"}`}
                data-testid={`persona-${p.value}`}
              >
                <div className="text-2xl">{p.emoji}</div>
                <div className="mt-1 font-heading text-sm font-bold text-ink-900">{p.label}</div>
              </button>
            ))}
          </div>
          <Button onClick={startSession} disabled={loading} className="w-full sm:w-auto" data-testid="btn-start-sim">
            <Sparkles className="mr-2 h-4 w-4" /> {loading ? "Memulai…" : "Mulai Latihan"}
          </Button>
        </Card>
      ) : (
        <Card className="overflow-hidden border-border">
          <div className="flex items-center justify-between border-b border-border bg-secondary/30 px-4 py-3">
            <div className="flex items-center gap-2">
              <div className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${currentPersona?.color || ""}`}>
                {currentPersona?.emoji} {session.persona_label}
              </div>
              {outcome === "deal" && <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">🎉 DEAL</span>}
              {outcome === "batal" && <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-700">❌ BATAL</span>}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={newSession} data-testid="btn-new-session"><X className="mr-1 h-3 w-3" /> Sesi Baru</Button>
              <Button size="sm" onClick={endAndEvaluate} disabled={loading || messages.length < 2} data-testid="btn-evaluate"><Award className="mr-1 h-3 w-3" /> Akhiri & Evaluasi</Button>
            </div>
          </div>
          <div ref={scrollRef} className="h-[480px] overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-secondary/20 to-transparent" data-testid="chat-area">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand to-brand-700 text-white"><Bot className="h-4 w-4" /></div>}
                <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.role === "user" ? "rounded-br-sm bg-brand text-white" : "rounded-bl-sm bg-card border border-border text-ink-900"}`}>{m.text}</div>
                {m.role === "user" && <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink-900 text-white"><User className="h-4 w-4" /></div>}
              </div>
            ))}
            {loading && <div className="flex gap-2 justify-start"><div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand to-brand-700 text-white"><Bot className="h-4 w-4" /></div><div className="rounded-2xl rounded-bl-sm bg-card border border-border px-4 py-2 text-sm text-ink-500">Mengetik…</div></div>}
          </div>
          <div className="border-t border-border bg-card p-3">
            <div className="flex gap-2">
              <Input value={draft} onChange={e=>setDraft(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();send();}}} placeholder="Ketik pesan sebagai Sales…" disabled={loading || outcome !== "ongoing"} data-testid="input-message" />
              <Button onClick={send} disabled={loading || !draft.trim() || outcome !== "ongoing"} data-testid="btn-send"><Send className="h-4 w-4" /></Button>
            </div>
            {outcome !== "ongoing" && <p className="mt-2 text-xs text-ink-500">Sesi sudah berakhir. Klik <b>Akhiri & Evaluasi</b> untuk feedback, atau <b>Sesi Baru</b> untuk mulai lagi.</p>}
          </div>
        </Card>
      )}

      {/* Evaluation modal */}
      <Dialog open={evalOpen} onOpenChange={setEvalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="eval-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" /> Evaluasi Performa Sales</DialogTitle>
            <DialogDescription>Analisis komprehensif dari AI Coach atas sesi latihan Anda.</DialogDescription>
          </DialogHeader>
          {evaluation && <EvaluationView ev={evaluation} outcome={outcome} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScorePill({ score }) {
  const s = Number(score) || 0;
  const tone = s >= 80 ? "from-emerald-500 to-emerald-700" : s >= 60 ? "from-sky-500 to-sky-700" : s >= 40 ? "from-amber-500 to-amber-600" : "from-rose-500 to-rose-700";
  return (
    <div className={`relative grid h-28 w-28 place-items-center rounded-full bg-gradient-to-br ${tone} text-white shadow-lg`}>
      <div className="text-center">
        <div className="font-heading text-4xl font-bold leading-none">{s}</div>
        <div className="text-[10px] uppercase tracking-widest opacity-90">/ 100</div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, items, color }) {
  if (!items?.length) return null;
  return (
    <Card className={`p-4 border ${color || "border-border"}`}>
      <p className="overline mb-2 flex items-center gap-1.5"><Icon className="h-3 w-3" /> {title}</p>
      <ul className="space-y-1.5 text-sm">
        {items.map((it, i) => <li key={i} className="flex gap-2"><span className="text-ink-500">•</span><span className="text-ink-900">{it}</span></li>)}
      </ul>
    </Card>
  );
}

function EvaluationView({ ev, outcome }) {
  if (ev.raw) return <pre className="whitespace-pre-wrap text-xs">{ev.raw}</pre>;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 rounded-lg border border-border bg-secondary/30 p-4">
        <ScorePill score={ev.skor} />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wider text-ink-500">Hasil Akhir</div>
          <div className="mt-0.5 font-heading text-lg font-bold">
            {outcome === "deal" ? "🎉 DEAL" : outcome === "batal" ? "❌ BATAL" : "—"}
          </div>
          <div className="mt-2 text-sm text-ink-700">{ev.ringkasan}</div>
        </div>
      </div>
      <Section icon={ThumbsUp} title="Kelebihan" items={ev.kelebihan} color="border-emerald-200 bg-emerald-50/30" />
      <Section icon={AlertTriangle} title="Kekurangan" items={ev.kekurangan} color="border-rose-200 bg-rose-50/30" />
      <Section icon={Lightbulb} title="Saran Perbaikan" items={ev.saran} color="border-amber-200 bg-amber-50/30" />
      <Section icon={Sparkles} title="Teknik yang Terlewatkan" items={ev.teknik_terlewatkan} color="border-violet-200 bg-violet-50/30" />
    </div>
  );
}
